import React, { useContext, useEffect, useState } from 'react'
import Navigation from '@App/navigation'
import { StoresContext } from '@App/contexts'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { CLEAR_WORK_DRAFT } from '@Const/work-registration-forms'
import ContentHeader from '@Components/ContentHeader'
import FileUploadBottomNavBar from '@Components/Navigations/CreateWorkBottomNavs/FileUploadBottomNavBar'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'

const CreateWorkByFileUploadLayout = () => {
    const { rightsholderStore, workStore, bulkUploadStore } = useContext(StoresContext)
    const location = useLocation()
    const navigate = useNavigate()
    const [currentStep, setCurrentStep] = useState<number>(0)
    const [currentStepType, setCurrentStepType] = useState<string>()
    const [backLink, setBackLink] = useState<string>()
    const [nextLink, setNextLink] = useState<string>()

    const setLinksToBar = (stepNumber: number) => {
        const currentRightsholderId = rightsholderStore.currentRightsholderId
        switch (stepNumber) {
            case 1:
                setBackLink(Navigation.rightholder.summary(currentRightsholderId))
                setNextLink(Navigation.rightholder.createWork.bulk.fileReview(currentRightsholderId))
                setCurrentStepType('File upload')
                break
            case 2:
                setBackLink(Navigation.rightholder.createWork.bulk.fileUpload(currentRightsholderId))
                setNextLink(Navigation.rightholder.createWork.bulk.submission(currentRightsholderId))
                setCurrentStepType('File review')
                break
            case 3:
                setNextLink(Navigation.rightholder.createWork.bulk.fileReview(currentRightsholderId))
                setCurrentStepType('Edit work')
                break
            case 4:
                setBackLink(Navigation.rightholder.createWork.bulk.fileReview(currentRightsholderId))
                setCurrentStepType('Submission')
                break
            default:
                setBackLink(Navigation.rightholder.createWork.bulk.root(currentRightsholderId))
                setNextLink(Navigation.rightholder.createWork.bulk.root(currentRightsholderId))
                setCurrentStepType('')
                break
        }
    }

    const getCurrentStep = (): number => {
        const currentRightsholderId = rightsholderStore.currentRightsholderId
        switch (location.pathname) {
            case Navigation.rightholder.createWork.bulk.root(currentRightsholderId):
            case Navigation.rightholder.createWork.bulk.fileUpload(currentRightsholderId):
                return 1
            case Navigation.rightholder.createWork.bulk.fileReview(currentRightsholderId):
                return 2
            case Navigation.rightholder.createWork.bulk.edit.root(currentRightsholderId):
                return 3
            case Navigation.rightholder.createWork.bulk.submission(currentRightsholderId):
                return 4
            default:
                // this value needs to be 0, it's ok that it doesn't match the switch case above, legacy magic number logic
                return 0
        }
    }

    const initWorkDraft = () => workStore.setWorkDraftToLocal(CLEAR_WORK_DRAFT)

    useEffect(() => {
        analyticsSendPage('rightsholder_registration_new_work_bulk')
        initWorkDraft()

        return () => {
            bulkUploadStore.destroyWorksOnCurrentPage()
            workStore.destroyDraftWork()
            bulkUploadStore.destroyStartTitlePositionWorkSubmissions()
        }
    }, [])

    useEffect(() => {
        const stepNumber = getCurrentStep()
        setCurrentStep(stepNumber)
        setLinksToBar(stepNumber)
    }, [location])

    const workDraftFromLocal = workStore.workDraftLocal

    if (!workDraftFromLocal) {
        return null
    }

    return (
        <>
            <title>Create work by form</title>
            <div className="create-work-scene">
                <div className="steps-wrapper">
                    {currentStep === 1 && (
                        <ContentHeader
                            title={`Create Work: ${currentStepType}`}
                            descriptionElement={
                                <>
                                    <p>
                                        Bulk Registration is where you can register up to 300 rows of data at one time.
                                    </p>
                                    <p>
                                        For formatting tips, please read the help content below. For general information
                                        on completing bulk work registration, you can review our Help Center article
                                        <a href="https://help.themlc.com/en/support/how-do-i-complete-the-bulk-work-document">
                                            {' '}
                                            here.
                                        </a>
                                    </p>
                                </>
                            }
                        />
                    )}
                    <Outlet />
                    {currentStep !== 0 && (
                        <FileUploadBottomNavBar
                            nextLink={nextLink}
                            backLink={backLink}
                            currentStep={currentStep}
                            navigate={navigate}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

export default CreateWorkByFileUploadLayout
